import { NextRouter, useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { QuerySortDropdownEnum } from '~/components/QuerySortDropdown';
import { Types } from '~/graphql';
import { SUPPORTED_LANGUAGES, type Language } from '~/core/constants/constants';

export type QueryFiltersParams = {
    search?: string | null;
    sortOrder: QuerySortDropdownEnum;
    handleSortOrderChange: (newSortOrder: QuerySortDropdownEnum) => void;
    topics: Array<string>;
    langs: Array<Language>;
    brands: Array<Types.ProductBrand>;
    partners: Array<string>;
    rating: string | undefined;
};

export type QueryParamFilters = {
    topics: Array<string>;
    langs: Array<Language>;
    brands: Array<Types.ProductBrand>;
    partners: Array<string>;
    rating: string | undefined;
    selectedCountry: string | undefined;
};

export const parseNonArrayQueryParam = (
    param: string | Array<string> | undefined
): string | undefined => {
    if (!param) {
        return undefined;
    }
    if (typeof param === 'string') {
        return param;
    }
    return param[0] ?? undefined;
};

export const parseQueryParam = (param: string | Array<string> | undefined): Array<string> => {
    if (!param) {
        return [];
    }
    if (typeof param === 'string') {
        return param.split(',').filter(Boolean);
    }
    return param.filter(Boolean);
};

export const getParamsFromQuery = (query: NextRouter['query']): QueryParamFilters => {
    return {
        topics: parseQueryParam(query.topics),
        langs: parseQueryParam(query.langs).filter((lang): lang is Language =>
            SUPPORTED_LANGUAGES.includes(lang as Language)
        ),
        brands: parseQueryParam(query.brands).map((brand) => brand as Types.ProductBrand),
        partners: parseQueryParam(query.partners),
        rating: parseNonArrayQueryParam(query.rating),
        selectedCountry: parseNonArrayQueryParam(query.selectedCountry)
    };
};

export const useGetFilterParamsFromQuery = (): QueryParamFilters => {
    const router = useRouter();
    const { topics, langs, brands, partners, rating, selectedCountry } = useMemo(
        () => getParamsFromQuery(router.query),
        [
            router.query.topics,
            router.query.langs,
            router.query.brands,
            router.query.partners,
            router.query.rating,
            router.query.selectedCountry
        ]
    );

    return {
        topics,
        langs,
        brands,
        partners,
        rating,
        selectedCountry
    };
};

export const useQueryFiltersParams = (): QueryFiltersParams => {
    const router = useRouter();
    const { topics, langs, brands, partners, rating } = useGetFilterParamsFromQuery();
    const { search_param, sortBy } = router.query;
    const [sortOrder, setSortOrder] = useState<QuerySortDropdownEnum>(
        sortBy === 'RELEVANT' ? QuerySortDropdownEnum.RELEVANT : QuerySortDropdownEnum.RECENT
    );

    useEffect(() => {
        setSortOrder(
            sortBy === 'RELEVANT' ? QuerySortDropdownEnum.RELEVANT : QuerySortDropdownEnum.RECENT
        );
    }, [sortBy]);

    const handleSortOrderChange = useCallback(
        (newSortOrder: QuerySortDropdownEnum) => {
            router.replace({
                query: {
                    ...router.query,
                    sortBy:
                        newSortOrder === QuerySortDropdownEnum.RECENT
                            ? QuerySortDropdownEnum.RECENT
                            : QuerySortDropdownEnum.RELEVANT
                }
            });
        },
        [router]
    );

    return {
        search: search_param as string,
        sortOrder,
        handleSortOrderChange,
        topics,
        langs,
        brands,
        partners,
        rating
    };
};
